import React, { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { DayButton, DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { addDays, eachDayOfInterval, endOfMonth, startOfDay, format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import api from '../../../api/axios';

export default function WorkDateSelector({
  employee_id,
  wfhWorkDates,
  isCheckedInToday = false,
  onRefresh,
  setOpenModalAction,
}) {
  const DEFAULT_WORK_LOCATION_ID = '2';
  const WEEKDAYS = [
    { label: 'Mon', value: 'monday' },
    { label: 'Tue', value: 'tuesday' },
    { label: 'Wed', value: 'wednesday' },
    { label: 'Thu', value: 'thursday' },
    { label: 'Fri', value: 'friday' },
    { label: 'Sat', value: 'saturday' },
    { label: 'Sun', value: 'sunday' },
  ];
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState([]);
  const [deselectDates, setDeselectDates] = useState([]);
  const [dateSelectorMode, setDateSelectorMode] = useState('today');
  const [workLocationOptions, setWorkLocationOptions] = useState([]);
  const [selectedWorkLocation, setSelectedWorkLocation] = useState(DEFAULT_WORK_LOCATION_ID);
  const [hybridDays, setHybridDays] = useState({});
  const [applyThisMonth, setApplyThisMonth] = useState(false);
  const [workLocationLoading, setWorkLocationLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [confirmDeselect, setConfirmDeselect] = useState(null); // { date, id, dateStr }

  const timeZone = 'Asia/Kolkata';

  const open = useCallback(() => {
    const today = startOfDay(toZonedTime(new Date(), timeZone));
    setShow(true);
    setMsg(null);
    setDateSelectorMode('today');
    setDeselectDates([]);
    setSelected(today ? [today] : []);
    setSelectedWorkLocation(DEFAULT_WORK_LOCATION_ID);
    setHybridDays({});
    setApplyThisMonth(false);
  }, [timeZone]);
  const close = () => setShow(false);

  useEffect(() => {
    if (typeof setOpenModalAction === 'function') {
      setOpenModalAction(() => open);
    }
  }, [open, setOpenModalAction]);

  const normalizeDate = useCallback((date) => {
    try {
      // Ensure input is a valid Date object
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        throw new Error('Invalid date input');
      }
      const zonedDate = toZonedTime(date, timeZone);
      return startOfDay(zonedDate);
    } catch (error) {
      console.error('Error normalizing date:', error);
      return null; // Return null for invalid dates
    }
  }, [timeZone]);

  const getTodayNormalized = useCallback(() => normalizeDate(new Date()), [normalizeDate]);

  const getDateString = (date) => format(date, 'yyyy-MM-dd');

  const getMonthStartDate = useCallback(() => {
    const today = getTodayNormalized();
    if (!today) {
      return null;
    }

    return isCheckedInToday ? addDays(today, 1) : today;
  }, [getTodayNormalized, isCheckedInToday]);

  const isHybridSelected = useCallback((selectedId) => {
    if (!selectedId) return false;
    const found = workLocationOptions.find((option) => String(option.id) === String(selectedId));
    return String(found?.name || '').trim().toLowerCase() === 'hybrid';
  }, [workLocationOptions]);

  const handleHybridDayType = (day, workLocationId) => {
    setHybridDays((prev) => {
      if (prev[day] === workLocationId) {
        const next = { ...prev };
        delete next[day];
        return next;
      }

      return { ...prev, [day]: workLocationId };
    });
  };

  const getSelectedWorkLocationName = useCallback(() => {
    const selectedOption = workLocationOptions.find((option) => String(option.id) === String(selectedWorkLocation));
    return selectedOption?.name || 'Work mode';
  }, [selectedWorkLocation, workLocationOptions]);

  const getHybridDaysPayload = () =>
    Object.entries(hybridDays).map(([day_name, work_location_id]) => ({
      day_name,
      work_location_id,
    }));

  const wfhDates = wfhWorkDates ? wfhWorkDates
    .filter((wfh) => !isNaN(new Date(wfh.work_date).getTime())) // Filter out invalid dates
    .map((wfh) => ({
      date: normalizeDate(new Date(wfh.work_date)),
      id: wfh?.wfh_id,
      approved_status: wfh?.approved_status,
      work_location_id: wfh?.work_location_id,
      work_location_name: wfh?.work_location_name,
    }))
    .filter((wfh) => wfh.date !== null) : null; // Remove null dates

  const getScheduledWorkLocationForDate = useCallback((date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }

    if (!isHybridSelected(selectedWorkLocation)) {
      return selectedWorkLocation;
    }

    const weekday = format(date, 'EEEE').toLowerCase();
    return hybridDays[weekday] || null;
  }, [hybridDays, isHybridSelected, selectedWorkLocation]);

  const getSavedWorkLocationForDate = useCallback((date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return null;
    }

    const dateString = getDateString(date);
    const matchingEntries = (wfhDates || []).filter((entry) => getDateString(entry.date) === dateString);

    if (matchingEntries.length === 0) {
      return null;
    }

    const savedEntry = [...matchingEntries].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0))[0];

    return savedEntry?.work_location_id != null ? String(savedEntry.work_location_id) : null;
  }, [wfhDates]);

  const getDayWorkModeLabel = useCallback((date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      return 'Work Mode';
    }

    const isSelectedDate = selected.some((selectedDate) => getDateString(startOfDay(selectedDate)) === getDateString(date));
    const savedWorkLocationId = getSavedWorkLocationForDate(date);
    const previewWorkLocationId = getScheduledWorkLocationForDate(date);
    const effectiveWorkLocationId = savedWorkLocationId || (isSelectedDate ? previewWorkLocationId : null);

    if (!effectiveWorkLocationId) {
      return '';
    }

    const effectiveOption = workLocationOptions.find((option) => String(option.id) === String(effectiveWorkLocationId));
    const effectiveName = effectiveOption?.name || getSelectedWorkLocationName();

    if (!isHybridSelected(selectedWorkLocation)) {
      return `Work Mode: ${effectiveName}`;
    }

    const workMode = effectiveWorkLocationId === '2' || effectiveWorkLocationId === 2
      ? 'WFH'
      : effectiveWorkLocationId === '1' || effectiveWorkLocationId === 1
        ? 'Onsite'
        : effectiveName;

    return `Work Mode: Hybrid${effectiveWorkLocationId ? ` (${workMode})` : ''}`;
  }, [getSavedWorkLocationForDate, getScheduledWorkLocationForDate, getSelectedWorkLocationName, isHybridSelected, selected, selectedWorkLocation, workLocationOptions]);

  const CalendarDayButton = useCallback((props) => {
    const title = getDayWorkModeLabel(props?.day?.date);
    return <DayButton {...props} title={title || undefined} />;
  }, [getDayWorkModeLabel]);

  const todayNormalized = getTodayNormalized();
  const currentMonthEnd = todayNormalized ? endOfMonth(todayNormalized) : null;
  const todayWeekday = todayNormalized ? format(todayNormalized, 'EEEE').toLowerCase() : null;
  const hybridScheduleDaysToShow = dateSelectorMode === 'today' && todayWeekday
    ? WEEKDAYS.filter((day) => day.value === todayWeekday)
    : WEEKDAYS;
  const todayDateString = todayNormalized ? getDateString(todayNormalized) : null;
  const todayWfhEntries = todayDateString
    ? (wfhDates?.filter((wfh) => getDateString(wfh.date) === todayDateString) || [])
    : [];
  const todayWfhEntry = todayWfhEntries.length > 0
    ? [...todayWfhEntries].sort((a, b) => {
      const aHasLocation = a?.work_location_id != null || String(a?.work_location_name || '').trim() !== '';
      const bHasLocation = b?.work_location_id != null || String(b?.work_location_name || '').trim() !== '';

      if (aHasLocation !== bHasLocation) {
        return Number(bHasLocation) - Number(aHasLocation);
      }

      return Number(b?.id || 0) - Number(a?.id || 0);
    })[0]
    : null;
  const todayWorkLocationId = todayWfhEntry?.work_location_id;
  const todayWorkLocationName = todayWfhEntry?.work_location_name;
  const getDefaultWorkLocationValue = useCallback((options) => {
    const defaultOption = options.find((option) => String(option?.id) === DEFAULT_WORK_LOCATION_ID);
    if (defaultOption?.id != null) {
      return DEFAULT_WORK_LOCATION_ID;
    }

    return options?.[0]?.id != null ? String(options[0].id) : DEFAULT_WORK_LOCATION_ID;
  }, []);

  const resolveWorkLocationForMode = useCallback((mode, options, prevValue) => {
    const safeOptions = Array.isArray(options) ? options : [];

    if (mode === 'custom') {
      return getDefaultWorkLocationValue(safeOptions);
    }

    if (todayWorkLocationId != null) {
      const byId = safeOptions.find((option) => String(option?.id) === String(todayWorkLocationId));
      if (byId?.id != null) {
        return String(byId.id);
      }
    }

    if (todayWorkLocationName) {
      const byName = safeOptions.find((option) => (
        String(option?.name || '').trim().toLowerCase()
        === String(todayWorkLocationName || '').trim().toLowerCase()
      ));
      if (byName?.id != null) {
        return String(byName.id);
      }
    }

    const fallback = getDefaultWorkLocationValue(safeOptions);
    if (fallback) {
      return fallback;
    }

    return prevValue || DEFAULT_WORK_LOCATION_ID;
  }, [getDefaultWorkLocationValue, todayWorkLocationId, todayWorkLocationName]);
  const isTodayAlreadyApplied =
    dateSelectorMode === 'today'
    && (todayWfhEntry?.approved_status === 'pending' || todayWfhEntry?.approved_status === 'approved');
  const isTodayCheckInBlocked = dateSelectorMode === 'today' && isCheckedInToday;

  useEffect(() => {
    const fetchWorkLocations = async () => {
      if (!show) {
        return;
      }
      setWorkLocationLoading(true);
      try {
        const response = await api.get('employees/working/location-type');
        const options = response?.data?.data || [];
        setWorkLocationOptions(options);
        setSelectedWorkLocation((prev) => resolveWorkLocationForMode(dateSelectorMode, options, prev));
      } catch (error) {
        console.error('Error fetching work location types:', error);
        setWorkLocationOptions([]);
      } finally {
        setWorkLocationLoading(false);
      }
    };

    fetchWorkLocations();
  }, [show, dateSelectorMode, resolveWorkLocationForMode]);

  useEffect(() => {
    if (!show || workLocationOptions.length === 0) {
      return;
    }

    setSelectedWorkLocation((prev) => resolveWorkLocationForMode(dateSelectorMode, workLocationOptions, prev));
  }, [show, dateSelectorMode, workLocationOptions, resolveWorkLocationForMode]);

  useEffect(() => {
    if (!isHybridSelected(selectedWorkLocation)) {
      setHybridDays({});
    }
  }, [selectedWorkLocation, isHybridSelected]);

  const applyMonthSelection = useCallback(() => {
    const monthStart = getMonthStartDate();
    if (!monthStart || !currentMonthEnd || monthStart > currentMonthEnd) {
      setSelected([]);
      setDeselectDates([]);
      return;
    }

    const monthDates = eachDayOfInterval({ start: monthStart, end: currentMonthEnd });
    const filteredDates = isHybridSelected(selectedWorkLocation)
      ? monthDates.filter((date) => {
          const weekday = format(date, 'EEEE').toLowerCase();
          return Boolean(hybridDays[weekday]);
        })
      : monthDates;
    setSelected(filteredDates);
    setDeselectDates([]);
  }, [currentMonthEnd, getMonthStartDate, hybridDays, isHybridSelected, selectedWorkLocation]);

  useEffect(() => {
    if (dateSelectorMode !== 'custom') {
      return;
    }

    if (applyThisMonth) {
      applyMonthSelection();
    }
  }, [applyThisMonth, applyMonthSelection, dateSelectorMode, hybridDays, selectedWorkLocation]);

  useEffect(() => {
    if (dateSelectorMode !== 'custom') {
      return;
    }

    if (!applyThisMonth) {
      setSelected([]);
      setDeselectDates([]);
    }
  }, [applyThisMonth, dateSelectorMode]);

  const handleModeChange = (mode) => {
    setDateSelectorMode(mode);
    setSelectedWorkLocation((prev) => resolveWorkLocationForMode(mode, workLocationOptions, prev));

    if (mode === 'today') {
      setDeselectDates([]);
      return;
    }

    // In custom mode, do not auto-select today's date.
    if (mode === 'custom') {
      setSelected([]);
    }
  };

  useEffect(() => {
    if (!show || dateSelectorMode !== 'today') {
      return;
    }

    if (isTodayAlreadyApplied || isTodayCheckInBlocked) {
      setSelected([]);
      setDeselectDates([]);
      return;
    }

    const today = getTodayNormalized();
    setSelected(today ? [today] : []);
    setDeselectDates([]);
  }, [show, dateSelectorMode, isTodayAlreadyApplied, isTodayCheckInBlocked, getTodayNormalized]);

  const handleCustomDayClick = (day, _modifiers, event) => {
    if (dateSelectorMode !== 'custom') {
      return;
    }
    if (applyThisMonth) {
      event?.currentTarget?.blur?.();
      return;
    }
    const clickedDay = startOfDay(day);
    const today = getTodayNormalized();
    if (!clickedDay || isNaN(clickedDay.getTime())) {
      console.error('Invalid date selected:', day);
      return; // Skip invalid dates
    }
    if (today && clickedDay < today) {
      event?.currentTarget?.blur?.();
      return;
    }
    if (today && isCheckedInToday && getDateString(clickedDay) === getDateString(today)) {
      event?.currentTarget?.blur?.();
      return;
    }

    if (isHybridSelected(selectedWorkLocation) && !getScheduledWorkLocationForDate(clickedDay)) {
      setMsg({ type: 'warning', text: 'Please select WFH or Onsite for this day first.' });
      event?.currentTarget?.blur?.();
      return;
    }

    const dateStr = getDateString(clickedDay);
    const wfhEntry = wfhDates ? wfhDates.find((wfh) => getDateString(wfh.date) === dateStr) : null;

    if (wfhEntry?.approved_status === 'rejected') {
      event?.currentTarget?.blur?.();
      return;
    }

    if (wfhEntry) {
      // Handle WFH date: toggle in deselectDates
      const existsInDeselect = deselectDates.find(
        (d) => getDateString(d.date) === dateStr
      );
      if (existsInDeselect) {
        // Already queued for removal — clicking again cancels the removal
        setDeselectDates((prev) =>
          prev.filter((d) => getDateString(d.date) !== dateStr)
        );
      } else if (wfhEntry.approved_status === 'approved') {
        // Show confirmation dialog before deselecting an approved WFH
        setConfirmDeselect({ date: clickedDay, id: wfhEntry.id, dateStr });
      } else {
        // Pending dates can be removed directly without confirmation
        setDeselectDates((prev) => [...prev, { date: clickedDay, id: wfhEntry.id }]);
      }
      event?.currentTarget?.blur?.();
      return;
    }

    // Toggle single-date selection: click again to remove selection.
    const isAlreadySelected = selected.some(
      (selectedDate) => getDateString(startOfDay(selectedDate)) === dateStr
    );
    if (isAlreadySelected) {
      setSelected((prev) =>
        prev.filter((selectedDate) => getDateString(startOfDay(selectedDate)) !== dateStr)
      );
      event?.currentTarget?.blur?.();
      return;
    }
    setSelected((prev) => [...prev, clickedDay]);
    event?.currentTarget?.blur?.();
  };

  const handleSave = async () => {
    if (isTodayCheckInBlocked) {
      setMsg({ type: 'warning', text: 'You are already checked in today. You cannot apply to change work mode for today.' });
      return;
    }

    if (isTodayAlreadyApplied) {
      setMsg({ type: 'info', text: 'You already applied for today. You cannot submit again.' });
      return;
    }

    const assigned_id = localStorage.getItem('employeeID');
    if (selected.length === 0 && deselectDates.length === 0) {
      setMsg({ type: 'warning', text: 'Please select at least one date.' });
      return;
    }
    if (!selectedWorkLocation) {
      setMsg({ type: 'warning', text: 'Please select work location mode.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      // Combine selected (new dates) and deselectDates (WFH dates with IDs)
      const selectedPayload = selected
        .filter((date) => !isNaN(date.getTime()))
        .map((date) => {
          const resolvedWorkLocation = getScheduledWorkLocationForDate(date);
          return resolvedWorkLocation
            ? { date: format(date, 'yyyy-MM-dd'), id: null, work_location: resolvedWorkLocation }
            : null;
        })
        .filter(Boolean);

      if (selectedPayload.length === 0 && deselectDates.length === 0) {
        setMsg({ type: 'warning', text: 'Please select at least one date with a work location.' });
        return;
      }

      const payload = [
        ...selectedPayload,
        ...deselectDates
          .filter((d) => !isNaN(d.date.getTime()))
          .map((d) => ({ date: format(d.date, 'yyyy-MM-dd'), id: d.id })),
      ].sort((a, b) => a.date.localeCompare(b.date));

      const requestBody = {
        dates: payload,
        ...(isHybridSelected(selectedWorkLocation) ? {} : { work_location: selectedWorkLocation }),
      };

      await api.post(`company/work_date?employee_id=${employee_id}&assigned_id=${assigned_id}`, {
        ...requestBody,
        ...(isHybridSelected(selectedWorkLocation) ? { hybrid_days: getHybridDaysPayload() } : {}),
      });
      if (deselectDates?.length > 0) {
        await api.put(`company/deselect_wfh_date?employee_id=${employee_id}&assigned_id=${assigned_id}`, {
          dates: deselectDates,
        });
      }
      if (onRefresh) {
        onRefresh();
      }
      setMsg({ type: 'success', text: 'Dates saved successfully.' });
      setSelected([]);
      setDeselectDates([]);
      close();
    } catch (err) {
      console.error(err);
      setMsg({ type: 'danger', text: err?.response?.data?.message || 'Save failed' });
    } finally {
      setLoading(false);
    }
  };

  const loggedInEmployeeId = localStorage.getItem('employeeID');
  const isSelfEmployee =
    employee_id != null
    && loggedInEmployeeId != null
    && String(employee_id) === String(loggedInEmployeeId);

  // Define modifiers for DayPicker
  const modifiers = {
    approved_wfh: wfhDates?.filter(
      (wfh) =>
        !deselectDates?.some(
          (d) => getDateString(d.date) === getDateString(wfh.date)
        ) && wfh.approved_status === 'approved'
    )
      .map((wfh) => wfh?.date),

    pending_wfh: wfhDates?.filter(
      (wfh) =>
        !deselectDates.some(
          (d) => getDateString(d.date) === getDateString(wfh.date)
        ) && wfh?.approved_status === 'pending'
    )
      .map((wfh) => wfh.date),

    rejected_wfh: wfhDates?.filter(
      (wfh) =>
        !deselectDates?.some(
          (d) => getDateString(d.date) === getDateString(wfh.date)
        ) && wfh?.approved_status === 'rejected'
    )
      .map((wfh) => wfh?.date),

    // Existing WFH dates that are neither approved, pending nor rejected.
    wfh: wfhDates?.filter(
      (wfh) =>
        !deselectDates?.some(
          (d) => getDateString(d.date) === getDateString(wfh.date)
        ) && (wfh.approved_status !== 'pending' && wfh.approved_status !== 'approved' && wfh.approved_status !== 'rejected')
    )
      .map((wfh) => wfh?.date),

    nonWFHSelected: selected,


  };

  const modifiersClassNames = {
    approved_wfh: 'is-approved',
    wfh: 'is-existing',
    pending_wfh: 'is-pending',
    rejected_wfh: 'is-rejected',
    nonWFHSelected: 'is-selected',
  };

  const rejectedDates = wfhDates?.filter((wfh) => wfh?.approved_status === 'rejected')
    .map((wfh) => wfh?.date) || [];

  const disabledDateMatchers = [
    ...(todayNormalized && isCheckedInToday ? [todayNormalized] : []),
    ...rejectedDates,
  ];

  const calendarStyles = {
    root: {
      width: '100%',
    },
    month: {
      width: '100%',
    },
    caption: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.55rem',
      padding: '0 0.3rem',
    },
    caption_label: {
      fontSize: '1.05rem',
      fontWeight: 800,
      color: '#334155',
      letterSpacing: '0.01em',
    },
    nav: {
      display: 'flex',
      gap: '0.45rem',
    },
    nav_button: {
      width: '30px',
      height: '30px',
      borderRadius: '999px',
      border: '1px solid #d5dfeb',
      background: '#ffffff',
      color: '#1d4ed8',
      padding: 0,
    },
    table: {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: '2px 6px',
    },
    head_cell: {
      color: '#7b8794',
      fontSize: '0.72rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.02em',
      paddingBottom: '0.3rem',
    },
    cell: {
      textAlign: 'center',
      padding: 0,
    },
    day: {
      width: '40px',
      height: '40px',
      borderRadius: '999px',
      border: '1px solid transparent',
      color: '#294168',
      fontWeight: 700,
      fontSize: '0.96rem',
      background: '#f9fbfd',
      transition: 'all 0.15s ease',
    },
    day_selected: {
      background: '#f9fbfd',
      color: '#294168',
      border: '1px solid transparent',
    },
    day_range_start: {
      background: '#f9fbfd',
      color: '#294168',
      borderRadius: '999px',
      border: '1px solid transparent',
    },
    day_range_end: {
      background: '#f9fbfd',
      color: '#294168',
      borderRadius: '999px',
      border: '1px solid transparent',
    },
    day_range_middle: {
      background: 'transparent',
      color: '#294168',
      borderRadius: '999px',
      border: 'none',
    },
    day_today: {
      border: '2px solid #0f8b8d',
      color: '#0f8b8d',
      fontWeight: 800,
    },
    day_outside: {
      color: '#b6c0cf',
    },
    day_disabled: {
      color: '#c4ccd6',
      opacity: 0.6,
    },
    selected: {
      background: 'transparent',
    },
    range_start: {
      background: 'transparent',
    },
    range_middle: {
      background: 'transparent',
    },
    range_end: {
      background: 'transparent',
    },
  };

  const selectedSummaryDates = [...selected]
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => a - b);

  const deselectedSummaryDates = [...deselectDates.map((d) => d.date)]
    .filter((date) => !isNaN(date.getTime()))
    .sort((a, b) => a - b);

  const formatSummaryDateList = (dates) => {
    if (dates.length === 1) {
      return format(dates[0], 'MMM dd, yyyy');
    }
    return dates
      .map((date) => format(date, 'MMM dd'))
      .join(', ');
  };

  const handleConfirmDeselect = () => {
    if (confirmDeselect) {
      setDeselectDates((prev) => [...prev, { date: confirmDeselect.date, id: confirmDeselect.id }]);
      setConfirmDeselect(null);
    }
  };

  const handleCancelDeselect = () => {
    setConfirmDeselect(null);
  };

  return (
    <>
    {/* Confirmation Dialog for deselecting approved WFH */}
    <Modal show={!!confirmDeselect} onHide={handleCancelDeselect} size="sm" centered backdropClassName="wfh-confirm-backdrop">
      <Modal.Body style={{ padding: '1.5rem 1.25rem', textAlign: 'center', background: '#fff', borderRadius: '12px' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: '48px', height: '48px', borderRadius: '50%',
            background: '#fef2f2', marginBottom: '0.75rem'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 7v5" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="16" r="1" fill="#ef4444"/>
            </svg>
          </span>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#111827', marginBottom: '0.35rem' }}>
            Remove Work Mode Date?
          </div>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.5 }}>
            Are you sure you want to remove the approved Work Mode for{' '}
            <strong style={{ color: '#374151' }}>
              {confirmDeselect ? format(confirmDeselect.date, 'MMM dd, yyyy') : ''}
            </strong>
            ? This action will cancel the approved work mode for this date.
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '1.25rem' }}>
          <Button
            variant="light"
            onClick={handleCancelDeselect}
            style={{
              borderRadius: '8px', padding: '0.4rem 1.2rem',
              fontSize: '0.84rem', fontWeight: 600,
              color: '#4b5563', border: '1px solid #d1d5db', minWidth: '80px'
            }}
          >
            No
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDeselect}
            style={{
              borderRadius: '8px', padding: '0.4rem 1.2rem',
              fontSize: '0.84rem', fontWeight: 600, minWidth: '80px',
              background: '#ef4444', borderColor: '#ef4444'
            }}
          >
            Yes, Remove
          </Button>
        </div>
      </Modal.Body>
    </Modal>

    <Modal show={show} onHide={close} size="md" centered>
      <Modal.Header
        
        style={{ backgroundColor: '#f6f8fb', borderBottom: '1px solid #e9ecef', padding: '0.75rem 1rem' }}
      >
        <div>
          <Modal.Title style={{ fontSize: '1.08rem', fontWeight: 700, color: '#1f2937' }}>
            Work Mode
          </Modal.Title>
          <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>
            Configure schedule and work location
          </small>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: '0.9rem 1rem', background: '#f6f8fb' }}>
        <div className="rounded-3 border" style={{ background: '#fff', borderColor: '#e7edf3', padding: '0.85rem' }}>
          {msg && (
            <div className={`alert alert-${msg.type} py-2 mb-3`} role="alert" style={{ fontSize: '0.86rem' }}>
              {msg.text}
            </div>
          )}

          <div className="mb-2 d-flex justify-content-between align-items-center">
            <label className="form-label fw-semibold mb-0" style={{ fontSize: '0.76rem', color: '#374151', letterSpacing: '0.02em' }}>
              SCHEDULE PERIOD
            </label>
            <div
              className="d-flex p-1 rounded-2"
              style={{ background: '#ebeff5', gap: '4px', minWidth: '140px' }}
              role="group"
              aria-label="Date selector mode"
            >
              <button
                type="button"
                className="btn btn-sm py-1 px-2 flex-fill"
                style={{
                  background: dateSelectorMode === 'today' ? '#1f9ea8' : 'transparent',
                  color: dateSelectorMode === 'today' ? '#fff' : '#45515c',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.73rem'
                }}
                onClick={() => handleModeChange('today')}
              >
                Today
              </button>
              <button
                type="button"
                className="btn btn-sm py-1 px-2 flex-fill"
                style={{
                  background: dateSelectorMode === 'custom' ? '#1f9ea8' : 'transparent',
                  color: dateSelectorMode === 'custom' ? '#fff' : '#45515c',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.73rem'
                }}
                onClick={() => handleModeChange('custom')}
              >
                Custom
              </button>
            </div>
          </div>

          {dateSelectorMode === 'custom' ? (
            <div
              className="workdate-calendar rounded-3 border mb-3"
              style={{
                padding: '0.75rem',
                background: '#f2f5f9',
                borderColor: '#e1e7ef',
              }}
            >
              <DayPicker
                mode="multiple"
                selected={selected}
                onDayClick={handleCustomDayClick}
                disabled={disabledDateMatchers}
                fromDate={todayNormalized || undefined}
                toDate={currentMonthEnd || undefined}
                styles={calendarStyles}
                components={{ DayButton: CalendarDayButton }}
                showOutsideDays
                fixedWeeks
                weekStartsOn={1}
                style={{ margin: '0 auto', maxWidth: '312px' }}
                modifiers={modifiers}
                modifiersClassNames={modifiersClassNames}
              />
              <div className="d-flex align-items-center justify-content-center mt-2" style={{ gap: '12px', fontSize: '0.72rem', color: '#5f6b76' }}>
                <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffffff', border: '2px solid #16a34a', display: 'inline-block' }} />
                  Approved
                </span>
                <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffffff', border: '2px solid #facc15', display: 'inline-block' }} />
                  Pending
                </span>
                <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffffff', border: '2px solid #2563eb', display: 'inline-block' }} />
                  Selected
                </span>
                <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffffff', border: '2px solid #fb7185', display: 'inline-block' }} />
                  Rejected
                </span>
              </div>
            </div>
          ) : (
            <div className="alert alert-info py-2 mb-3" role="alert" style={{ fontSize: '0.82rem' }}>
              {isTodayCheckInBlocked
                ? 'You are already checked in today. You cannot apply to change work mode for today.'
                : todayWfhEntry
                ? `You have already applied for today${todayWfhEntry.approved_status === 'pending'
                  ? ' (approval pending). You cannot apply again.'
                  : todayWfhEntry.approved_status === 'approved'
                    ? ' (already approved). You cannot apply again.'
                    : '.'}`
                : `Request date is set to today: ${selected[0] ? format(selected[0], 'yyyy-MM-dd') : '-'}`}
            </div>
          )}

          <div className="mb-3">
            <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.76rem', color: '#374151', letterSpacing: '0.02em' }}>
              WORK LOCATION
            </label>
            <select
              className="form-select form-select-sm"
              value={selectedWorkLocation}
              onChange={(e) => setSelectedWorkLocation(e.target.value)}
              disabled={workLocationLoading || isTodayAlreadyApplied || isTodayCheckInBlocked}
              style={{ borderRadius: '8px', minHeight: '34px' }}
            >
              <option value="" disabled>Select Work Location</option>
              {workLocationOptions.map((option) => (
                <option value={option.id} key={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>

          {isHybridSelected(selectedWorkLocation) && (
            <div className="mb-3">
              <label className="form-label fw-semibold mb-2" style={{ fontSize: '0.76rem', color: '#374151', letterSpacing: '0.02em' }}>
                SELECT HYBRID SCHEDULE
                <small className="text-muted fw-normal ms-1">(choose WFH or Onsite for each day)</small>
              </label>
              <div className="rounded-3 border p-2" style={{ background: '#f8f9fa', borderColor: '#dee2e6' }}>
                <div className="d-flex flex-column gap-1">
                  {hybridScheduleDaysToShow.map((day) => (
                    <div
                      key={day.value}
                      className="d-flex align-items-center gap-2 rounded-2 px-2 py-1"
                      style={{
                        width: 'fit-content',
                        background: hybridDays[day.value] ? '#fff' : 'transparent',
                        border: hybridDays[day.value] ? '1px solid #dee2e6' : '1px solid transparent',
                      }}
                    >
                      <span style={{ minWidth: '32px', fontSize: '0.8rem', fontWeight: 600, color: '#495057' }}>
                        {day.label}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleHybridDayType(day.value, 2)}
                        className="btn btn-sm py-0 px-2"
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          borderRadius: '4px',
                          background: hybridDays[day.value] === 2 ? '#0d6efd' : '#e9ecef',
                          color: hybridDays[day.value] === 2 ? '#fff' : '#6c757d',
                          border: 'none',
                          lineHeight: '1.8',
                        }}
                      >
                        WFH
                      </button>
                      <button
                        type="button"
                        onClick={() => handleHybridDayType(day.value, 1)}
                        className="btn btn-sm py-0 px-2"
                        style={{
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          borderRadius: '4px',
                          background: hybridDays[day.value] === 1 ? '#198754' : '#e9ecef',
                          color: hybridDays[day.value] === 1 ? '#fff' : '#6c757d',
                          border: 'none',
                          lineHeight: '1.8',
                        }}
                      >
                        Onsite
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              {Object.keys(hybridDays).length === 0 && (
                <span className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>
                  * Select at least one day with WFH or Onsite
                </span>
              )}
              {dateSelectorMode === 'custom' && (
                <>
                  <div className="d-flex align-items-center justify-content-between mt-2 mb-1" style={{ gap: '10px' }}>
                    <label className="d-flex align-items-center" style={{ gap: '8px', fontSize: '0.78rem', fontWeight: 600, color: '#334155', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={applyThisMonth}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setApplyThisMonth(checked);
                        }}
                        style={{ width: '14px', height: '14px', accentColor: '#1f9ea8' }}
                      />
                      Apply this month
                    </label>
                  </div>
                  {applyThisMonth && (
                    <div className="mb-2" style={{ paddingLeft: '2px' }}>
                      <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                        Auto-selects from {getMonthStartDate() ? format(getMonthStartDate(), 'MMM dd') : '-'} to {currentMonthEnd ? format(currentMonthEnd, 'MMM dd') : '-'}
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          

          {!isTodayAlreadyApplied && !isTodayCheckInBlocked && (selectedSummaryDates.length > 0 || deselectedSummaryDates.length > 0) && (
            <div
              className="rounded-3"
              style={{
                background: '#edf2fb',
                borderLeft: '3px solid #1f9ea8',
                padding: '0.55rem 0.7rem',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '0.75rem', color: '#2b3f56', marginBottom: '0.2rem' }}>
                Schedule Summary
              </div>
              {selectedSummaryDates.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#4b6076', lineHeight: 1.4 }}>
                  {selectedSummaryDates.length === 1
                    ? `Selected date: ${formatSummaryDateList(selectedSummaryDates)}`
                    : `Selected dates (${selectedSummaryDates.length}): ${formatSummaryDateList(selectedSummaryDates)}`}
                </div>
              )}
              {deselectedSummaryDates.length > 0 && (
                <div style={{ fontSize: '0.72rem', color: '#4b6076', lineHeight: 1.4 }}>
                  {deselectedSummaryDates.length === 1
                    ? `Deselected date: ${formatSummaryDateList(deselectedSummaryDates)}`
                    : `Deselected dates (${deselectedSummaryDates.length}): ${formatSummaryDateList(deselectedSummaryDates)}`}
                </div>
              )}
            </div>
          )}
        </div>

        <style>{`
            .workdate-calendar .rdp-day {
              border: none !important;
              box-shadow: none !important;
              background: transparent !important;
            }
            .workdate-calendar .rdp-day_button {
              border-radius: 999px !important;
              background: #f9fbfd !important;
              color: #294168 !important;
              border: 1px solid transparent !important;
            }
            .workdate-calendar .rdp-day_button:hover,
            .workdate-calendar .rdp-day_button:focus,
            .workdate-calendar .rdp-day_button:focus-visible,
            .workdate-calendar .rdp-day_button:active {
              box-shadow: none !important;
              outline: none !important;
              color: #294168 !important;
            }
            .workdate-calendar .rdp-selected {
              background: transparent !important;
            }

            .workdate-calendar .rdp-day.is-approved .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-approved {
              background: #fff !important;
              color: #166534 !important;
              border: 2px solid #16a34a !important;
            }

            .workdate-calendar .rdp-day.is-existing .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-existing {
              background: #fff !important;
              color: #1e3a8a !important;
              border: 2px solid #2f5bff !important;
            }

            .workdate-calendar .rdp-day.is-pending .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-pending {
              background: #fff !important;
              color: #a16207 !important;
              border: 2px solid #facc15 !important;
            }

            .workdate-calendar .rdp-day.is-rejected .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-rejected {
              background: #fff !important;
              color: #be123c !important;
              border: 2px solid #fb7185 !important;
            }

            .workdate-calendar .rdp-day.is-selected .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-selected {
              background: #fff !important;
              color: #1d4ed8 !important;
              border: 2px solid #2563eb !important;
            }
            .workdate-calendar .rdp-day.is-selected .rdp-day_button:hover,
            .workdate-calendar .rdp-day.is-selected .rdp-day_button:focus,
            .workdate-calendar .rdp-day.is-selected .rdp-day_button:focus-visible,
            .workdate-calendar .rdp-day.is-selected .rdp-day_button:active,
            .workdate-calendar .rdp-day_button.is-selected:hover,
            .workdate-calendar .rdp-day_button.is-selected:focus,
            .workdate-calendar .rdp-day_button.is-selected:focus-visible,
            .workdate-calendar .rdp-day_button.is-selected:active {
              background: #fff !important;
              color: #1d4ed8 !important;
              border: 2px solid #2563eb !important;
            }

            .workdate-calendar .rdp-day.is-approved .rdp-day_button,
            .workdate-calendar .rdp-day.is-existing .rdp-day_button,
            .workdate-calendar .rdp-day.is-pending .rdp-day_button,
            .workdate-calendar .rdp-day.is-rejected .rdp-day_button,
            .workdate-calendar .rdp-day.is-selected .rdp-day_button,
            .workdate-calendar .rdp-day_button.is-approved,
            .workdate-calendar .rdp-day_button.is-existing,
            .workdate-calendar .rdp-day_button.is-pending,
            .workdate-calendar .rdp-day_button.is-rejected,
            .workdate-calendar .rdp-day_button.is-selected {
              border-radius: 999px !important;
              box-shadow: none !important;
              outline: none !important;
            }
          `}</style>

      </Modal.Body>
      <Modal.Footer style={{ borderTop: '1px solid #e9ecef', background: '#f6f8fb', padding: '0.75rem 1rem' }}>
        <Button
          variant="light"
          onClick={close}
          style={{ borderRadius: '0.5rem', padding: '0.35rem 1rem', fontSize: '0.82rem', color: '#4b5563', border: '1px solid #d7dde5' }}
        >
          Close
        </Button>
        <Button
          variant="success"
          onClick={handleSave}
          disabled={loading || isTodayAlreadyApplied || isTodayCheckInBlocked}
          style={{ borderRadius: '0.5rem', padding: '0.35rem 1rem', fontWeight: '600', fontSize: '0.82rem', background: '#1f9ea8', borderColor: '#1f9ea8' }}
        >
          {loading ? 'Please wait...' : isSelfEmployee ? 'Request Dates' : 'Save Dates'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}